import React, { useRef } from 'react';
import { X, Download, Printer } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ReceiptModal = ({ receipt, onClose }) => {
  const receiptRef = useRef(null);

  const handleDownloadPDF = async () => {
    const element = receiptRef.current;
    const canvas = await html2canvas(element, { scale: 2, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Receipt-${receipt.receipt_number}.pdf`);
  };

  const handlePrint = () => {
    const printContent = receiptRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Receipt - ${receipt.receipt_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 40px; color: #111; }
            .receipt-container { max-width: 600px; margin: 0 auto; border: 2px solid #e5e7eb; border-radius: 12px; padding: 40px; }
            .receipt-header { text-align: center; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px; }
            .receipt-header h1 { font-size: 24px; margin: 0 0 4px 0; }
            .receipt-header p { color: #6b7280; margin: 4px 0; font-size: 14px; }
            .receipt-number { background: #f3f4f6; padding: 8px 16px; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 14px; margin-top: 12px; }
            .receipt-body { margin: 20px 0; }
            .receipt-row { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #f3f4f6; }
            .receipt-row .label { color: #6b7280; font-size: 14px; }
            .receipt-row .value { font-weight: 600; font-size: 14px; text-align: right; }
            .amount-section { background: #f9fafb; border-radius: 12px; padding: 20px; margin: 20px 0; text-align: center; }
            .amount-section .amount { font-size: 32px; font-weight: bold; }
            .amount-section .label { color: #6b7280; font-size: 13px; margin-top: 4px; }
            .verified-badge { background: #dcfce7; color: #15803d; padding: 6px 16px; border-radius: 20px; display: inline-block; font-weight: 600; font-size: 13px; margin-top: 16px; }
            .receipt-footer { text-align: center; margin-top: 24px; padding-top: 20px; border-top: 2px solid #e5e7eb; color: #9ca3af; font-size: 12px; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const formattedDate = new Date(receipt.enrollment_date).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const isCancelled = receipt.cancelled;
  const formattedCancelDate = receipt.cancelled_at
    ? new Date(receipt.cancelled_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="receipt-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{isCancelled ? 'Cancellation Receipt' : 'Payment Receipt'}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 flex items-center gap-2"
              data-testid="print-receipt-btn"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleDownloadPDF}
              className="px-4 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 flex items-center gap-2"
              data-testid="download-receipt-btn"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700"
              data-testid="close-receipt-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div ref={receiptRef}>
            <div className="receipt-container" style={{ maxWidth: '600px', margin: '0 auto', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '40px' }}>
              <div className="receipt-header" style={{ textAlign: 'center', borderBottom: '2px solid #e5e7eb', paddingBottom: '20px', marginBottom: '20px' }}>
                <h1 style={{ fontSize: '24px', margin: '0 0 4px 0', fontFamily: 'Arial, sans-serif' }}>{receipt.academy_name}</h1>
                <p style={{ color: '#6b7280', margin: '4px 0', fontSize: '14px', fontFamily: 'Arial, sans-serif' }}>{isCancelled ? 'Cancellation Receipt' : 'Payment Receipt'}</p>
                <div className="receipt-number" style={{ background: '#f3f4f6', padding: '8px 16px', borderRadius: '8px', display: 'inline-block', fontWeight: 'bold', fontSize: '14px', marginTop: '12px', fontFamily: 'Arial, sans-serif' }}>
                  {receipt.receipt_number}
                </div>
              </div>

              <div className="receipt-body" style={{ margin: '20px 0', fontFamily: 'Arial, sans-serif' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Student Name</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{receipt.student_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Email</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{receipt.student_email}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Course</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{receipt.course_title}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Level</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{receipt.course_level}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Payment Method</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{receipt.payment_method === 'upi' ? 'UPI (Indian Rupees)' : 'Pi Cryptocurrency'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Date</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{formattedDate}</span>
                </div>
                {receipt.transaction_id && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ color: '#6b7280', fontSize: '14px' }}>Transaction ID</span>
                    <span style={{ fontWeight: '600', fontSize: '14px', fontFamily: 'monospace' }}>{receipt.transaction_id}</span>
                  </div>
                )}
                {isCancelled && formattedCancelDate && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ color: '#6b7280', fontSize: '14px' }}>Cancellation Date</span>
                    <span style={{ fontWeight: '600', fontSize: '14px', color: '#dc2626' }}>{formattedCancelDate}</span>
                  </div>
                )}
              </div>

              <div className="amount-section" style={{ background: '#f9fafb', borderRadius: '12px', padding: '20px', margin: '20px 0', textAlign: 'center' }}>
                {receipt.payment_method === 'upi' ? (
                  <>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', fontFamily: 'Arial, sans-serif' }}>
                      INR {receipt.amount_inr.toLocaleString('en-IN')}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px', fontFamily: 'Arial, sans-serif' }}>
                      ({receipt.amount_pi} Pi equivalent)
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', fontFamily: 'Arial, sans-serif' }}>
                      {receipt.amount_pi} Pi
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px', fontFamily: 'Arial, sans-serif' }}>
                      (INR {receipt.amount_inr.toLocaleString('en-IN')} equivalent)
                    </div>
                  </>
                )}
                <div style={{ marginTop: '4px', color: '#6b7280', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>Amount Paid</div>
                <div style={{ marginTop: '16px' }}>
                  {isCancelled ? (
                    <span style={{ background: '#fee2e2', color: '#dc2626', padding: '6px 16px', borderRadius: '20px', fontWeight: '600', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>
                      Admission Cancelled - Refund
                    </span>
                  ) : (
                    <span style={{ background: '#dcfce7', color: '#15803d', padding: '6px 16px', borderRadius: '20px', fontWeight: '600', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>
                      Payment Verified
                    </span>
                  )}
                </div>
              </div>

              <div className="receipt-footer" style={{ textAlign: 'center', marginTop: '24px', paddingTop: '20px', borderTop: '2px solid #e5e7eb', color: '#9ca3af', fontSize: '12px', fontFamily: 'Arial, sans-serif' }}>
                <p style={{ margin: '4px 0' }}>This is a computer-generated receipt and does not require a signature.</p>
                <p style={{ margin: '4px 0' }}>{receipt.academy_name} - German Language Courses</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
